import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Badge } from '@gto/ui/badge'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { ChevronDownIcon, ChevronUpIcon } from '@gto/ui/icons/outline'
import { Menu } from '@gto/ui/menu'

import { zIndex } from '@/utils/constants'

export type HeaderNavDropdownProps = {
  options: {
    label: string | ReactNode
    onClick?: () => void
    comingSoon?: boolean
    new?: boolean
  }[]
  label: string | ReactNode
  onLabelClick: () => void
}

export const HeaderNavDropdown = ({
  options,
  label = '',
  onLabelClick,
}: HeaderNavDropdownProps) => {
  const [open, setOpen] = useState(false)
  const dropdownIdRef = useRef(`dropdown-${Math.random().toString(36).substring(2, 11)}`)

  // Close the menu when the cursor moves over a sibling nav button (so opening
  // a different dropdown doesn't leave this one open alongside it).
  useEffect(
    function closeOnSiblingNavHover() {
      if (!open) {
        return
      }

      const handleMouseMove = (event: MouseEvent) => {
        const navButtons = document.querySelectorAll('[data-nav-dropdown]')
        const currentDropdownId = dropdownIdRef.current

        for (const button of navButtons) {
          if (button.getAttribute('data-dropdown-id') === currentDropdownId) {
            continue
          }

          const rect = button.getBoundingClientRect()
          const isHoveringOtherButton =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom

          if (isHoveringOtherButton) {
            setOpen(false)
            break
          }
        }
      }

      document.addEventListener('mousemove', handleMouseMove)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
      }
    },
    [open],
  )

  // Hover-only: ignore pressing the trigger so a misclick doesn't toggle the menu.
  const handleOpenChange = useCallback((nextOpen: boolean, details: Menu.MenuOpenChangeDetails) => {
    if (Menu.isTriggerPress(details)) {
      return
    }
    setOpen(nextOpen)
  }, [])

  const handleLabelClick = useCallback(() => {
    onLabelClick()
    setOpen(false)
  }, [onLabelClick])

  return (
    <Menu.Root
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Menu.Trigger
        openOnHover
        closeDelay={150}
        render={
          <Button
            data-nav-dropdown
            data-dropdown-id={dropdownIdRef.current}
            size="small"
            variant="text"
            className={cn(
              'transition-colors duration-300 hover:bg-transparent',
              open && 'text-primary-main',
            )}
            endIcon={
              options.length > 0 ? (
                open ? (
                  <ChevronUpIcon height={14} />
                ) : (
                  <ChevronDownIcon height={14} />
                )
              ) : null
            }
            onClick={handleLabelClick}
          >
            {label}
          </Button>
        }
      />
      {options.length > 0 && (
        <Menu.Portal>
          {/* Keep the dropdown above rbwizard's high-z navbar (zIndex.header). */}
          <Menu.Positioner
            align="start"
            style={{ zIndex: zIndex.header + 1 }}
          >
            <Menu.Popup>
              <Menu.Group>
                {options.map((option, index) => {
                  const key = typeof option.label === 'string' ? option.label : `option-${index}`

                  return (
                    <Menu.Item
                      key={key}
                      onClick={option.onClick}
                    >
                      {option.label}
                      {(option.comingSoon ?? false) && (
                        <Badge
                          className="ml-2 align-middle"
                          color="warning"
                        >
                          <FormattedMessage id="common.labels.comingSoon" />
                        </Badge>
                      )}
                      {(option.new ?? false) && (
                        <Badge
                          className="ml-2 align-middle"
                          color="success"
                        >
                          <FormattedMessage id="common.labels.new" />
                        </Badge>
                      )}
                    </Menu.Item>
                  )
                })}
              </Menu.Group>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      )}
    </Menu.Root>
  )
}
